import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import SEO from "@/components/SEO";

export default function ResetPassword() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event from the auth hash
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === "PASSWORD_RECOVERY") {
          setReady(true);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      toast({ title: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }
    if (password !== confirm) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Password set successfully!", description: "You can now sign in to Mission Control." });
      navigate("/mission-control");
    }
    setLoading(false);
  }

  return (
    <main className="min-h-[70vh] flex items-center justify-center bg-background">
      <SEO title="Set Your Password" description="Set your password to access Mission Control." noindex />
      <div className="w-full max-w-md mx-auto px-4">
        <div className="border border-border rounded-2xl p-8 bg-background shadow-sm">
          <h1 className="text-2xl font-bold tracking-tight mb-2 text-center">Set Your Password</h1>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {ready
              ? "Choose a secure password for your Mission Control account."
              : "Verifying your invitation link…"}
          </p>

          {ready ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm Password</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="Re-enter your password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Setting password…" : "Set Password & Sign In"}
              </Button>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="animate-pulse text-muted-foreground text-sm">Processing…</div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}